import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {DialogModule} from '@progress/kendo-angular-dialog';
import {ProdutoService} from '../../services/produto.service';
import {PedidosService} from '../../services/pedidos.service';
import {PedidosProduzirService} from '../../services/pedidos-produzir.service';

@Component({
  selector: 'app-pedido-interno',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule],
  templateUrl: './pedido-interno.component.html',
  styleUrls: ['./pedido-interno.component.scss']
})
export class PedidoInternoComponent implements OnInit {
  @Input() empresa: any;
  @Output() fechou = new EventEmitter<void>();
  @Output() criado = new EventEmitter<any>();
  produtos: any[] = [];
  categorias: {nome: string; total: number}[] = [];
  categoriaSelecionada: string = null;
  itens: any[] = [];
  opcoes: any;
  comissionadoId: number = null;
  observacoes = '';
  busca = '';
  carregando = true;
  salvando = false;
  erro = '';

  constructor(private produtoService: ProdutoService, private pedidosService: PedidosService,
              private producaoService: PedidosProduzirService) {}

  ngOnInit() {
    Promise.all([this.produtoService.listeAhVenda('ADMIN', null, null, null, true),
      this.producaoService.opcoesProducao()]).then(([catalogo, opcoes]: any[]) => {
      this.opcoes = opcoes;
      this.produtos = (catalogo?.produtos || []).filter(p => p.momentoBaixaEstoque === 'producao' &&
        p.tipo !== 'kit' && p.tipo !== 'pizza' && !(p.tamanhos || []).length);
      const categorias = new Map<string, number>();
      this.produtos.forEach(produto => {
        const nome = this.nomeCategoria(produto);
        categorias.set(nome, (categorias.get(nome) || 0) + 1);
      });
      this.categorias = Array.from(categorias, ([nome, total]) => ({nome, total}));
      if(!opcoes.producaoPedidosAtiva)
        this.erro = 'Ative a produção de pedidos no módulo Estoque.';
    }).catch(erro => this.erro = erro?.message || erro || 'Não foi possível carregar os produtos.')
      .finally(() => this.carregando = false);
  }

  get produtosFiltrados() {
    const termo = this.busca.trim().toLocaleLowerCase();
    return this.produtos.filter(p => (!this.categoriaSelecionada || this.nomeCategoria(p) === this.categoriaSelecionada) &&
      (!termo || p.nome.toLocaleLowerCase().includes(termo) || String(p.codigoPdv || p.codigo || p.id).toLocaleLowerCase().includes(termo)));
  }

  nomeCategoria(produto: any): string {
    return String(produto.categoria?.nome || produto.categoria || '').trim() || 'Sem categoria';
  }

  private produtoOriginal(produto: any): any {
    return this.produtos.find(p => p.id === produto.id) || produto;
  }

  imagemProduto(produto: any): string {
    const original = this.produtoOriginal(produto);
    if(original.imagemIndisponivel) return null;
    const link = original.imagens?.[0]?.linkImagem || original.linkImagem || original.imagem;
    if(!link) return null;
    return /^https?:\/\//i.test(link) ? link : '/images/empresa/' + String(link).replace(/^\/+/, '');
  }

  imagemIndisponivel(produto: any) {
    this.produtoOriginal(produto).imagemIndisponivel = true;
  }

  unidadeProduto(produto: any): string {
    return this.produtoOriginal(produto).unidadeMedida?.sigla || 'un';
  }

  quantidadeProduto(produto: any): number {
    return Number(this.itens.find(item => item.produto.id === produto.id)?.qtde) || 0;
  }

  get resumoQuantidades(): string {
    const quantidades = new Map<string, number>();
    this.itens.forEach(item => {
      const unidade = this.unidadeProduto(item.produto);
      quantidades.set(unidade, (quantidades.get(unidade) || 0) + (Number(item.qtde) || 0));
    });
    return Array.from(quantidades, ([unidade, qtde]) => `${qtde.toLocaleString('pt-BR', {maximumFractionDigits: 3})} ${unidade}`).join(' · ');
  }

  altereQuantidade(item: any, variacao: number) {
    if(this.salvando) return;
    const qtde = Number(item.qtde) + variacao;
    if(Number.isFinite(qtde) && qtde > 0) item.qtde = qtde;
  }

  remova(item: any) {
    if(!this.salvando) this.itens = this.itens.filter(i => i !== item);
  }

  feche() {
    if(!this.salvando) this.fechou.emit();
  }

  adicione(produto: any) {
    if(this.salvando) return;
    const existente = this.itens.find(i => i.produto.id === produto.id);
    if(existente) existente.qtde = Number(existente.qtde || 0) + 1;
    else this.itens.push({produto: {id: produto.id, nome: produto.nome}, qtde: 1, observacao: ''});
    this.erro = '';
  }

  gere() {
    if(this.salvando || this.carregando) return;
    this.erro = '';
    if(!this.opcoes?.producaoPedidosAtiva) {
      this.erro = 'A produção de pedidos não está ativa.';
      return;
    }
    if(!this.itens.length || this.itens.some(i => !Number.isFinite(Number(i.qtde)) || Number(i.qtde) <= 0)) {
      this.erro = 'Adicione produtos e informe uma quantidade positiva para cada um.';
      return;
    }
    if(this.opcoes.moduloComissionamentoAtivo &&
      !this.opcoes.comissionados.some(p => Number(p.id) === Number(this.comissionadoId))) {
      this.erro = 'Selecione o responsável pela produção.';
      return;
    }
    this.salvando = true;
    this.pedidosService.criePedidoInterno({itens: this.itens.map(item => ({produto: item.produto, qtde: item.qtde, observacao: item.observacao})), observacoes: this.observacoes,
      comissionado: this.comissionadoId ? {id: this.comissionadoId} : null})
      .then(pedido => this.criado.emit(pedido))
      .catch(erro => this.erro = erro?.message || erro || 'Não foi possível gerar o pedido interno.')
      .finally(() => this.salvando = false);
  }
}
